// Tiny non-hook fetch helpers for command mutation handlers. Hooks
// can't run inside handler.run() callbacks because handlers are
// invoked imperatively. We rely on Clerk's window-level session
// resolution via `window.Clerk` instead. This is the same posture
// the global SSE handler uses.
//
// The handler emits the same toast affordances the rest of the
// app uses, and re-throws so the command invoker can record a
// failed-invocation telemetry event.

import { toast } from "sonner";

type ClerkWindow = Window & {
  Clerk?: {
    session?: { getToken: () => Promise<string | null> } | null;
  };
};

async function getToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const w = window as ClerkWindow;
  const s = w.Clerk?.session;
  if (!s) return null;
  try {
    return await s.getToken();
  } catch {
    return null;
  }
}

async function authedHeaders(): Promise<HeadersInit> {
  const token = await getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function commandPost(
  path: string,
  body?: unknown,
): Promise<unknown> {
  const res = await fetch(path, {
    method: "POST",
    headers: await authedHeaders(),
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(payload.error || res.statusText);
  }
  if (res.status === 204) return undefined;
  return res.json().catch(() => undefined);
}

export async function commandPatch(
  path: string,
  body?: unknown,
): Promise<unknown> {
  const res = await fetch(path, {
    method: "PATCH",
    headers: await authedHeaders(),
    body: body == null ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(payload.error || res.statusText);
  }
  if (res.status === 204) return undefined;
  return res.json().catch(() => undefined);
}

export function toastOk(message: string) {
  toast.success(message);
}

export function toastError(message: string) {
  toast.error(message);
}
